'use client';

import { FC } from 'react';
import { useSearchParams } from 'next/navigation';

import { TEMPLATE_BASE_PATH } from '@/constants';
import { useBundleSpecial, useSelectedStore } from '@/hooks';
import { useUiStoreSelector } from '@/data';
import {
  Breadcrumb,
  CollectionTreez,
  Container,
  FiltersTreez,
  Heading,
  Link,
  SpecialPlaceholder,
} from '@/components';
import {
  buildQueryFilterFromSearchParams,
  buildSortFilterFromSearchParams,
  Configure,
  OpenSearchProvider,
} from '@/opensearch';
import { sanitizeTreezSearchParams } from '@/utils';

import { BREADCRUMB_ITEMS } from './contants';

const SpecialComponents = () => {
  const searchParams = useSearchParams();
  const searchParamsObject = Object.fromEntries(
    Array.from(searchParams.entries()).filter(([key]) => key !== 'special_id'),
  );
  const sanitizedSearchParams = sanitizeTreezSearchParams({
    searchParams: searchParamsObject,
  });
  const sortFilter = buildSortFilterFromSearchParams(sanitizedSearchParams);

  const bundleId = searchParams?.get('special_id') ?? '';
  const { isLoading, special, processingIndependents } = useBundleSpecial({
    id: bundleId,
  });

  const dependentProductsIds =
    special?.depProducts?.map(prod => prod.id!) ?? [];
  const independentProductsIds =
    special?.indProducts?.map(prod => prod.id!) ?? [];
  const productsIds = processingIndependents
    ? independentProductsIds
    : dependentProductsIds;

  const queryFiltersByFacets = buildQueryFilterFromSearchParams({
    ...sanitizedSearchParams,
    id: productsIds,
  });

  const fixedQueryFiltersByFacets = buildQueryFilterFromSearchParams({
    isHideFromMenu: [false],
    isAboveThreshold: [true],
  });

  return (
    <main id="main" role="main">
      <Container as="section" className="special_page">
        <Breadcrumb items={BREADCRUMB_ITEMS}>{special?.title ?? ''}</Breadcrumb>
      </Container>
      {isLoading ? (
        <SpecialPlaceholder />
      ) : special ? (
        <OpenSearchProvider
          includePromoItems
          initialFixedQueryFiltersByFacets={fixedQueryFiltersByFacets}
          initialQueryFiltersByFacets={queryFiltersByFacets}
          initialSearchFilter={searchParams.get('query') || ''}
          initialSortFilter={sortFilter}
        >
          <Configure filters={{ id: productsIds }} hitsPerPage={100} />
          <FiltersTreez />
          <CollectionTreez bundleId={bundleId} onSpecials />
        </OpenSearchProvider>
      ) : (
        <SpecialsEmptyMessage />
      )}
    </main>
  );
};

const SpecialsEmptyMessage: FC = () => {
  const { store } = useSelectedStore();

  return (
    <Container as="section" className="special_empty">
      <Heading level={5}>
        {store?.shortName
          ? 'Special not found'
          : 'Select your store to unlocked this bundle'}
      </Heading>
      {store?.shortName ? <VisitShopPage /> : <SelectYourStore />}
    </Container>
  );
};

const VisitShopPage: FC = () => {
  return (
    <div className="special_empty__actions">
      Click &nbsp;
      <Link className="action" href={TEMPLATE_BASE_PATH.SHOP}>
        here to visit the Shop Page.
      </Link>
    </div>
  );
};

const SelectYourStore: FC = () => {
  const { setState } = useUiStoreSelector([]);

  const openStoreLocation = () => {
    setState({ is_store_location_open: true });
  };

  return (
    <p className="special_empty__actions">
      Click &nbsp;
      <span className="action" onClick={openStoreLocation} role="button">
        here to select your store.
      </span>
    </p>
  );
};

export default SpecialComponents;
